import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import * as couponApis from 'apis/coupons';
import * as planApis from 'apis/plans';
import * as subscriptionApis from 'apis/subscriptions';
import './Coupons.scss';

/*
  Coupons page, converted from the live-test.html mockup.

  The page is split in two because the two halves do different jobs:

    - "My coupons" (GET /v1/coupons/mine) are wide ticket tiles with a
      perforated stub carrying the code, its reveal/copy actions and the
      redemption state - what a member opens this page for.
    - "Available to claim" (GET /v1/coupons/available) are compact tiles
      with fixed content zones, so every tile in a row lines up.

  Two things the mockup showed are not reproduced, because the API does not
  expose them: the "12 of 50 left" scarcity ribbon (findAvailableForUser
  counts claims to filter the list but does not return the count) and
  named plan eligibility for coupons tied to a retired plan - /v1/plans/active
  only returns plans still open to new members, so those fall back to
  "Selected plans".

  The legacy page is kept, unrouted, in Coupons.backup.js.
*/

const COUPON_TYPE = {
  FIXED: 'fixed',
  PERCENTAGE: 'percentage',
  FREEBIE: 'freebie',
};

const TYPE_LABELS = {
  [COUPON_TYPE.FIXED]: 'Flat off',
  [COUPON_TYPE.PERCENTAGE]: '% off',
  [COUPON_TYPE.FREEBIE]: 'Freebie',
};

const CLAIM_GROUP = {
  READY: 'ready',
  USED: 'used',
  EXPIRED: 'expired',
};

const DAY = 24 * 60 * 60 * 1000;

// Inside this many days a coupon is worth flagging - on the tile as a
// ribbon, and in the summary strip as a count.
const ENDING_SOON_DAYS = 14;
const EXPIRING_SOON_DAYS = 30;
const NEW_FOR_DAYS = 14;

const COPIED_RESET_MS = 1600;

const SKELETON_COUNT = 3;

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// The decimal columns come back as strings ("1200.00"), so they go through
// Number() before grouping rather than being printed raw.
const formatAmount = (value) => `₹${Number(value).toLocaleString('en-IN')}`;

const daysUntil = (value) => {
  const date = toDate(value);
  return date ? Math.ceil((date.getTime() - Date.now()) / DAY) : null;
};

const errorMessage = (error, fallback) => {
  // Nest's ValidationPipe sends an array of messages, the service's own
  // BadRequestException sends a single string.
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  return message || fallback;
};

// Mirrors getMaxRedemptions() in coupon.service.ts. The wallet has to agree
// with what the partner's scanner will accept, so this rule is copied
// deliberately rather than approximated.
const maxRedemptions = (coupon) =>
  coupon?.isMultiUse ? (coupon.maxRedemptionsPerClaim ?? 1) : 1;

const offerValue = (coupon) => {
  if (coupon?.type === COUPON_TYPE.FIXED) {
    return { value: formatAmount(coupon.discountValue), suffix: 'OFF' };
  }
  if (coupon?.type === COUPON_TYPE.PERCENTAGE) {
    return { value: `${Number(coupon.discountValue)}%`, suffix: 'OFF' };
  }
  return { value: coupon?.freebieLabel || 'Freebie', suffix: '', isText: true };
};

/*
  The state the partner's verify endpoint would report for a claim, mapped
  to the group its wallet tab belongs to. Fully spent comes before expiry:
  a coupon the member actually used reads better as used than as expired.
*/
const claimState = (claim) => {
  const coupon = claim?.coupon;
  const max = maxRedemptions(coupon);
  const used = claim?.redemptionCount || 0;
  const remainingDays = daysUntil(coupon?.expiresAt);

  if (used >= max) {
    return {
      group: CLAIM_GROUP.USED,
      tone: '',
      icon: 'fa-check-double',
      label: max > 1 ? 'Fully redeemed' : 'Redeemed',
    };
  }

  if (remainingDays !== null && remainingDays <= 0) {
    return { group: CLAIM_GROUP.EXPIRED, tone: 'is-expired', icon: 'fa-circle-xmark', label: 'Expired' };
  }

  if (coupon?.isActive === false) {
    return { group: CLAIM_GROUP.EXPIRED, tone: 'is-expired', icon: 'fa-circle-xmark', label: 'Not available' };
  }

  if (used > 0) {
    return {
      group: CLAIM_GROUP.READY,
      tone: 'is-partial',
      icon: 'fa-circle-half-stroke',
      label: `${used} of ${max} used`,
    };
  }

  return { group: CLAIM_GROUP.READY, tone: 'is-unused', icon: 'fa-circle-check', label: 'Unused' };
};

const claimUsesLabel = (claim) => {
  const max = maxRedemptions(claim?.coupon);
  if (max <= 1) return 'Single use';
  return `${Math.max(max - (claim?.redemptionCount || 0), 0)} of ${max} left`;
};

const couponUsesLabel = (coupon) => {
  const max = maxRedemptions(coupon);
  return max > 1 ? `${max} uses` : 'Single use';
};

const minSpendLabel = (coupon) =>
  coupon?.minPurchaseAmount != null ? formatAmount(coupon.minPurchaseAmount) : 'No minimum';

const eligibilityLabel = (coupon, planNames) => {
  const ids = coupon?.eligiblePlanIds;
  if (!ids?.length) return 'All plans';

  const names = ids.map((id) => planNames[id]).filter(Boolean);
  return names.length === ids.length ? names.join(', ') : 'Selected plans';
};

// One ribbon per tile, in order of how much it should move the member.
const couponRibbon = (coupon) => {
  const remainingDays = daysUntil(coupon?.expiresAt);
  if (remainingDays !== null && remainingDays > 0 && remainingDays <= ENDING_SOON_DAYS) {
    return {
      tone: 'is-soon',
      icon: 'fa-hourglass-half',
      label: remainingDays === 1 ? 'Ends tomorrow' : `${remainingDays} days left`,
    };
  }

  const created = toDate(coupon?.createdAt);
  if (created && Date.now() - created.getTime() <= NEW_FOR_DAYS * DAY) {
    return { tone: '', icon: '', label: 'New' };
  }

  if (maxRedemptions(coupon) > 1) {
    return { tone: 'is-limited', icon: 'fa-rotate', label: 'Multi use' };
  }

  if (coupon?.eligiblePlanIds?.length) {
    return { tone: '', icon: 'fa-crown', label: 'Plan exclusive' };
  }

  return null;
};

const stubNote = (claim, state) => {
  const lastRedeemed = toDate(claim?.lastRedeemedAt);
  const lastRedeemedLabel = lastRedeemed ? ` Last redeemed ${formatDate(lastRedeemed)}.` : '';

  if (state.group === CLAIM_GROUP.USED) {
    const max = maxRedemptions(claim?.coupon);
    return max > 1
      ? `All ${max} uses are spent.${lastRedeemedLabel}`
      : `This coupon has been redeemed.${lastRedeemedLabel}`;
  }

  const expiry = toDate(claim?.coupon?.expiresAt);
  if (expiry && daysUntil(claim?.coupon?.expiresAt) <= 0) {
    return `Expired on ${formatDate(expiry)}. ${claim?.coupon?.partner?.name || 'The partner'} may have a current offer.`;
  }

  return 'This coupon is no longer available to redeem.';
};

const SummaryStat = ({ icon, tone, value, label }) => (
  <div className="cp-stat">
    <span className={`cp-stat-icon${tone ? ` ${tone}` : ''}`}>
      <i className={`fa-solid ${icon}`}></i>
    </span>
    <div>
      <span className="cp-stat-num">{value}</span>
      <span className="cp-stat-label">{label}</span>
    </div>
  </div>
);

const FilterTabs = ({ tabs, active, onChange }) => (
  <div className="cp-tabs">
    {tabs.map(({ id, label, count }) => (
      <button
        type="button"
        key={id}
        className={`cp-tab${active === id ? ' is-active' : ''}`}
        onClick={() => onChange(id)}
      >
        {label} <b>{count}</b>
      </button>
    ))}
  </div>
);

const PartnerLogo = ({ partner, assetUrl }) => (
  <div className="cp-logo">
    {partner?.image
      ? <img src={`${assetUrl}${partner.image}`} alt={partner?.name || ''} />
      : <i className="fa-solid fa-store"></i>}
  </div>
);

const PartnerRow = ({ coupon, assetUrl, children }) => (
  <>
    <PartnerLogo partner={coupon?.partner} assetUrl={assetUrl} />
    <div className="cp-partner-text">
      <span className="cp-partner-name">{coupon?.partner?.name || coupon?.title}</span>
      {coupon?.partner?.category && (
        <span className="cp-partner-cat">{coupon.partner.category}</span>
      )}
    </div>
    {children}
  </>
);

const OfferValue = ({ coupon }) => {
  const { value, suffix, isText } = offerValue(coupon);

  return (
    <div className={`cp-value${isText ? ' is-text' : ''}`}>
      {value}
      {suffix && <>{' '}<small>{suffix}</small></>}
    </div>
  );
};

const MetaItem = ({ icon, label, value, warn }) => (
  <div>
    <span className="cp-meta-label"><i className={`fa-solid ${icon}`}></i>{label}</span>
    <span className={`cp-meta-value${warn ? ' is-warn' : ''}`}>{value}</span>
  </div>
);

const WalletTicket = ({ claim, assetUrl, revealed, copied, onToggleReveal, onCopy }) => {
  const { coupon } = claim;
  const state = claimState(claim);
  const isDone = state.group !== CLAIM_GROUP.READY;

  const expiry = toDate(coupon?.expiresAt);
  const claimedAt = toDate(claim.claimedAt);
  const bannerSrc = coupon?.image ? `${assetUrl}${coupon.image}` : '';
  const logoSrc = coupon?.partner?.image ? `${assetUrl}${coupon.partner.image}` : '';

  // The ticket's meta row has no slot for the minimum spend, so it doubles
  // as the condition line when the coupon has no subtitle of its own.
  const condition = coupon?.subtitle
    || (coupon?.minPurchaseAmount != null
      ? `On purchases above ${formatAmount(coupon.minPurchaseAmount)}`
      : '');

  return (
    <article className={`cp-ticket${isDone ? ' is-done' : ''}`}>

      <div className="cp-ticket-body">

        {bannerSrc && (
          <div className="cp-ticket-media" aria-hidden="true">
            <img src={bannerSrc} alt="" />
          </div>
        )}

        <div className="cp-partner">
          <PartnerRow coupon={coupon} assetUrl={assetUrl}>
            <span className={`cp-tag${coupon?.type === COUPON_TYPE.FREEBIE ? ' is-freebie' : ''}`}>
              {TYPE_LABELS[coupon?.type] || 'Offer'}
            </span>
          </PartnerRow>
        </div>

        <div>
          <OfferValue coupon={coupon} />
          {coupon?.title && <p className="cp-offer-title">{coupon.title}</p>}
          {condition && <p className="cp-offer-cond">{condition}</p>}
        </div>

        <div className="cp-meta">
          <MetaItem
            icon="fa-calendar-day"
            label="Valid till"
            value={expiry ? formatDate(expiry) : 'No expiry'}
            warn={state.group === CLAIM_GROUP.EXPIRED}
          />
          <MetaItem icon="fa-rotate" label="Uses" value={claimUsesLabel(claim)} />
          <MetaItem
            icon="fa-clock"
            label="Claimed"
            value={claimedAt ? formatDate(claimedAt) : '-'}
          />
        </div>

      </div>

      <div className="cp-stub">
        {logoSrc && <img className="cp-stub-mark" src={logoSrc} alt="" aria-hidden="true" />}
        <span className={`cp-status${state.tone ? ` ${state.tone}` : ''}`}>
          <i className={`fa-solid ${state.icon}`}></i>{state.label}
        </span>
        <span className="cp-code-label">Coupon code</span>

        <div className={`cp-code${isDone ? ' is-spent' : ''}${!isDone && !revealed ? ' is-masked' : ''}`}>
          {claim.code}
        </div>

        {isDone ? (
          <p className="cp-stub-note">{stubNote(claim, state)}</p>
        ) : (
          <div className="cp-stub-actions">
            <button type="button" className="cp-btn cp-btn-primary" onClick={onToggleReveal}>
              {revealed ? 'Hide code' : 'Show code'}
            </button>
            <button
              type="button"
              className={`cp-btn cp-btn-ghost${copied ? ' is-copied' : ''}`}
              aria-label={copied ? 'Code copied' : 'Copy code'}
              onClick={onCopy}
            >
              <i className={copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'}></i>
            </button>
          </div>
        )}
      </div>

    </article>
  );
};

const ClaimCard = ({ coupon, assetUrl, planNames, claiming, onClaim }) => {
  const [showTerms, setShowTerms] = useState(false);

  const assetSrc = coupon.image ? `${assetUrl}${coupon.image}` : '';
  const ribbon = couponRibbon(coupon);
  const expiry = toDate(coupon.expiresAt);
  const remainingDays = daysUntil(coupon.expiresAt);

  return (
    <article className="cp-card">

      <div className="cp-card-media">
        {ribbon && (
          <span className={`cp-ribbon${ribbon.tone ? ` ${ribbon.tone}` : ''}`}>
            {ribbon.icon && <i className={`fa-solid ${ribbon.icon}`}></i>}{ribbon.label}
          </span>
        )}
        {assetSrc && <img src={assetSrc} alt={coupon.title || ''} />}
      </div>

      <div className="cp-card-head">
        <PartnerRow coupon={coupon} assetUrl={assetUrl} />
      </div>

      <div className="cp-card-body">
        <OfferValue coupon={coupon} />
        {coupon.title && <p className="cp-offer-title">{coupon.title}</p>}
        {coupon.subtitle && <p className="cp-offer-cond">{coupon.subtitle}</p>}

        <div className="cp-meta">
          <MetaItem
            icon="fa-calendar-day"
            label="Valid till"
            value={expiry ? formatDate(expiry) : 'No expiry'}
            warn={remainingDays !== null && remainingDays <= ENDING_SOON_DAYS}
          />
          <MetaItem icon="fa-rotate" label="Uses" value={couponUsesLabel(coupon)} />
          <MetaItem icon="fa-indian-rupee-sign" label="Min spend" value={minSpendLabel(coupon)} />
          <MetaItem
            icon="fa-crown"
            label="Eligibility"
            value={eligibilityLabel(coupon, planNames)}
          />
        </div>

        {showTerms && <p className="cp-terms">{coupon.description}</p>}
      </div>

      <div className="cp-card-foot">
        <button
          type="button"
          className="cp-btn cp-btn-primary cp-btn-block"
          disabled={claiming}
          onClick={onClaim}
        >
          <i className="fa-solid fa-ticket"></i>
          {claiming ? 'Claiming...' : 'Claim coupon'}
        </button>
        {coupon.description && (
          <button
            type="button"
            className="cp-foot-link"
            aria-expanded={showTerms}
            onClick={() => setShowTerms((previous) => !previous)}
          >
            {showTerms ? 'Hide' : 'Terms'}
          </button>
        )}
      </div>

    </article>
  );
};

const EmptyState = ({ icon, title, children }) => (
  <div className="cp-empty">
    <i className={`fa-solid ${icon}`}></i>
    <h4>{title}</h4>
    {children}
  </div>
);

function Coupons() {
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;

  const [mine, setMine] = useState([]);
  const [available, setAvailable] = useState([]);
  const [planNames, setPlanNames] = useState({});
  const [hasActivePlan, setHasActivePlan] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [walletTab, setWalletTab] = useState('all');
  const [claimTab, setClaimTab] = useState('all');
  const [revealed, setRevealed] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [claimingId, setClaimingId] = useState(null);

  const copyTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [mineResponse, availableResponse] = await Promise.all([
        couponApis.fetchMyCoupons(),
        couponApis.fetchAvailableCoupons(),
      ]);
      setMine(mineResponse.data || []);
      setAvailable(availableResponse.data || []);
      setError('');
    } catch (err) {
      console.log(err);
      setError('Unable to load your coupons. Please refresh and try again.');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await load();
      setLoading(false);
    };

    fetchData();
  }, [load]);

  // Names for the eligibility line. A failure here is not worth surfacing:
  // eligibilityLabel() falls back to "Selected plans".
  useEffect(() => {
    const fetchPlanNames = async () => {
      try {
        const response = await planApis.fetchPlans();
        const names = {};
        (response.data || []).forEach((plan) => { names[plan.id] = plan.name; });
        setPlanNames(names);
      } catch (err) {
        console.log(err);
      }
    };

    fetchPlanNames();
  }, []);

  // findAvailableForUser() returns [] both when there is genuinely nothing
  // to claim and when the membership is not active, so the empty state needs
  // this to tell the member which of the two it is.
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await subscriptionApis.fetchSubscription();
        setHasActivePlan(response.data?.status === 'active');
      } catch (err) {
        setHasActivePlan(false);
      }
    };

    fetchSubscription();
  }, []);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const wallet = useMemo(
    () => mine.map((claim) => ({ claim, state: claimState(claim) })),
    [mine]
  );

  const walletCounts = useMemo(() => ({
    [CLAIM_GROUP.READY]: wallet.filter(({ state }) => state.group === CLAIM_GROUP.READY).length,
    [CLAIM_GROUP.USED]: wallet.filter(({ state }) => state.group === CLAIM_GROUP.USED).length,
    [CLAIM_GROUP.EXPIRED]: wallet.filter(({ state }) => state.group === CLAIM_GROUP.EXPIRED).length,
  }), [wallet]);

  const expiringSoonCount = useMemo(() => wallet.filter(({ claim, state }) => {
    if (state.group !== CLAIM_GROUP.READY) return false;
    const remainingDays = daysUntil(claim.coupon?.expiresAt);
    return remainingDays !== null && remainingDays <= EXPIRING_SOON_DAYS;
  }).length, [wallet]);

  const typeCounts = useMemo(() => available.reduce((counts, coupon) => ({
    ...counts,
    [coupon.type]: (counts[coupon.type] || 0) + 1,
  }), {}), [available]);

  // Only groups with something in them get a tab, and a single populated
  // group needs no tabs at all.
  const walletTabs = [
    { id: 'all', label: 'All', count: wallet.length },
    { id: CLAIM_GROUP.READY, label: 'Ready', count: walletCounts[CLAIM_GROUP.READY] },
    { id: CLAIM_GROUP.USED, label: 'Used', count: walletCounts[CLAIM_GROUP.USED] },
    { id: CLAIM_GROUP.EXPIRED, label: 'Expired', count: walletCounts[CLAIM_GROUP.EXPIRED] },
  ].filter(({ id, count }) => id === 'all' || count > 0);

  const claimTabs = [
    { id: 'all', label: 'All', count: available.length },
    { id: COUPON_TYPE.PERCENTAGE, label: '% off', count: typeCounts[COUPON_TYPE.PERCENTAGE] || 0 },
    { id: COUPON_TYPE.FIXED, label: 'Flat off', count: typeCounts[COUPON_TYPE.FIXED] || 0 },
    { id: COUPON_TYPE.FREEBIE, label: 'Freebies', count: typeCounts[COUPON_TYPE.FREEBIE] || 0 },
  ].filter(({ id, count }) => id === 'all' || count > 0);

  const visibleWallet = walletTab === 'all'
    ? wallet
    : wallet.filter(({ state }) => state.group === walletTab);

  const visibleAvailable = claimTab === 'all'
    ? available
    : available.filter((coupon) => coupon.type === claimTab);

  const toggleReveal = (claimId) => {
    setRevealed((previous) => ({ ...previous, [claimId]: !previous[claimId] }));
  };

  const handleCopy = async (claim) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(claim.code);
      } else {
        // The clipboard API needs a secure context, so older setups fall
        // back to a throwaway textarea.
        const scratch = document.createElement('textarea');
        scratch.value = claim.code;
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand('copy');
        document.body.removeChild(scratch);
      }

      setCopiedId(claim.id);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedId(null), COPIED_RESET_MS);
    } catch (err) {
      toast.error('Could not copy the code. Please note it down instead.');
    }
  };

  const handleClaim = async (coupon) => {
    if (claimingId) return;
    setClaimingId(coupon.id);

    try {
      const response = await couponApis.claimCoupon(coupon.id);
      const claim = response.data;

      // The claimed coupon leaves /available and appears under /mine, so
      // both lists have to come back before the wallet is right.
      await load();

      if (claim?.id) {
        // The member clicked to get the code, so it starts revealed.
        setRevealed((previous) => ({ ...previous, [claim.id]: true }));
      }
      setWalletTab('all');

      toast.success(claim?.code
        ? `Coupon claimed. Code ${claim.code} is now in My coupons.`
        : 'Coupon claimed and saved to My coupons.');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to claim coupon.'));
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-container">

        <div className="cp-head">
          <span className="cp-eyebrow">Member Wallet</span>
          <h1 className="cp-title">Coupons</h1>
          <p className="cp-sub">
            Claim a partner offer, then show the code at the counter - the partner
            verifies it on their side and marks it redeemed.
          </p>
        </div>

        {error && (
          <div className="cp-error" role="alert">
            <i className="fa-solid fa-circle-exclamation"></i>
            <span>{error}</span>
          </div>
        )}

        {!loading && (
          <div className="cp-stats">
            <SummaryStat icon="fa-wallet" value={wallet.length} label="In your wallet" />
            <SummaryStat
              icon="fa-circle-check"
              tone="is-ready"
              value={walletCounts[CLAIM_GROUP.READY]}
              label="Ready to use"
            />
            <SummaryStat icon="fa-tags" value={available.length} label="Available to claim" />
            <SummaryStat
              icon="fa-hourglass-half"
              tone="is-warn"
              value={expiringSoonCount}
              label="Expiring soon"
            />
          </div>
        )}

        {!!wallet.length && (
          <div className="cp-section">

            <div className="cp-section-head">
              <div className="cp-section-title"><i className="fa-solid fa-wallet"></i>My coupons</div>
              <span className="cp-rule"></span>
              {walletTabs.length > 2 && (
                <FilterTabs tabs={walletTabs} active={walletTab} onChange={setWalletTab} />
              )}
            </div>

            {visibleWallet.length ? (
              <div className="cp-wallet">
                {visibleWallet.map(({ claim }) => (
                  <WalletTicket
                    key={claim.id}
                    claim={claim}
                    assetUrl={assetUrl}
                    revealed={!!revealed[claim.id]}
                    copied={copiedId === claim.id}
                    onToggleReveal={() => toggleReveal(claim.id)}
                    onCopy={() => handleCopy(claim)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon="fa-wallet" title="Nothing under this filter">
                <p>Switch back to All to see every coupon you have claimed.</p>
              </EmptyState>
            )}

          </div>
        )}

        <div className="cp-section">

          <div className="cp-section-head">
            <div className="cp-section-title"><i className="fa-solid fa-tags"></i>Available to claim</div>
            <span className="cp-rule"></span>
            {claimTabs.length > 2 && (
              <FilterTabs tabs={claimTabs} active={claimTab} onChange={setClaimTab} />
            )}
          </div>

          {loading && (
            <div className="cp-grid">
              {Array.from({ length: SKELETON_COUNT }, (unused, index) => (
                <div className="cp-skeleton" key={`skeleton-${index}`} />
              ))}
            </div>
          )}

          {!loading && !!visibleAvailable.length && (
            <div className="cp-grid">
              {visibleAvailable.map((coupon) => (
                <ClaimCard
                  key={coupon.id}
                  coupon={coupon}
                  assetUrl={assetUrl}
                  planNames={planNames}
                  claiming={claimingId === coupon.id}
                  onClaim={() => handleClaim(coupon)}
                />
              ))}
            </div>
          )}

          {!loading && !visibleAvailable.length && !hasActivePlan && (
            <EmptyState icon="fa-lock" title="Your membership is not active">
              <p>
                Partner coupons are a member benefit. Start or renew a plan to claim
                them, and anything already in your wallet stays there.
              </p>
              <Link className="cp-empty-link" to="/dashboard/my-subscription">
                View plans <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </EmptyState>
          )}

          {!loading && !visibleAvailable.length && hasActivePlan && (
            <EmptyState
              icon="fa-tags"
              title={claimTab === 'all' ? 'No coupons to claim right now' : 'No coupons of this kind right now'}
            >
              <p>
                {claimTab === 'all'
                  ? 'New partner offers are added through the year - check back soon.'
                  : 'Try another filter to see what else is on offer.'}
              </p>
            </EmptyState>
          )}

        </div>

        <div className="cp-section">

          <div className="cp-section-head">
            <div className="cp-section-title">
              <i className="fa-solid fa-circle-info"></i>How redemption works
            </div>
            <span className="cp-rule"></span>
          </div>

          <div className="cp-how">

            <div className="cp-how-step">
              <span className="cp-how-num">1</span>
              <div>
                <h4>Claim the coupon</h4>
                <p>A code is generated for you alone and saved to your wallet. Claiming does not use the offer up.</p>
              </div>
            </div>

            <div className="cp-how-step">
              <span className="cp-how-num">2</span>
              <div>
                <h4>Show the code</h4>
                <p>Read out or show the code at the partner outlet before billing, along with your membership.</p>
              </div>
            </div>

            <div className="cp-how-step">
              <span className="cp-how-num">3</span>
              <div>
                <h4>Partner redeems it</h4>
                <p>They verify the code on their side and mark it used. The status here updates with it.</p>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}

export default Coupons;
